import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Input, Text } from 'rizzui';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import { ShowPermissionAccordion } from '../../../../../components/ui/PermissionsAccordion';
import useFetch from '../../../../../hooks/useFetch';
import FormGroup from '../../../../../layout/shared/form-group';
import { PermissionService } from '../../../../../services/auth/permissionService';
import { UserService } from '../../../../../services/user/userService';
import { Permission, PermissionGroup, PermissionsApiResponse, RolePermission } from '../../../../../type/entities/role-type';
import { UserApiResponse } from '../../../../../type/entities/user-type';
import cn from '../../../../../utils/classNames';
import UserProfileLayout from '../../UserProfileLayout';

const UserPermissions = () => {

    // STATES

    const [groupSearch, setGroupSearch] = useState<string>('');
    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
    const [filteredPermissionsData, setFilteredPermissionsData] = useState<PermissionGroup[] | null>(null);

    // HOOKS

    const { id = '' } = useParams<{ id: string }>();

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER LOS DATOS DEL USUARIO
     * @EN GET USER DATA
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new UserService()).getUserById(id);
        return response.getResponseData() as UserApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER LOS PERMISOS
     * @EN GET PERMISSIONS
     */
    //--------------------------------------------------------------------------------------------------------------
    const [permissionsData, permissionsLoading] = useFetch(useCallback(async () => {
        const response = await (new PermissionService()).getPermissions();
        return response.getResponseData() as PermissionsApiResponse;
    }, []));
    //--------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        if (data) setSelectedPermissions(data?.userPermissions.map((permission: RolePermission) => permission.permission.id));
    }, [data]);

    /**
     * Filter groups by groupSearch, if groupSearch is empty, return all groups
     */
    useEffect(() => {
        if (permissionsData) {
            if (groupSearch === '') {
                setFilteredPermissionsData(permissionsData);
            } else {
                const filteredGroups = permissionsData.filter((group: PermissionGroup) => { return group.label.toLowerCase().includes(groupSearch.toLowerCase()) });
                setFilteredPermissionsData(filteredGroups);
            }
        }
    }, [permissionsData, groupSearch]);

    // RENDER

    return (
        <>
            <div className="@container">
                <div>
                    <div className="mt-6 mb-4 flex items-center">
                        <Text className="rizzui-input-label block text-sm font-medium me-3">Buscar grupo:</Text>
                        <Input
                            id="groupSearch"
                            value={groupSearch}
                            onChange={(e) => setGroupSearch(e.target.value)}
                        />
                    </div>
                    {(filteredPermissionsData && !permissionsLoading)
                        ? filteredPermissionsData.map((permissionGroup: PermissionGroup) => {
                            const atLeastOneSelected = permissionGroup.permissions.some((permission: Permission) => selectedPermissions.includes(permission.id));

                            return (
                                <FormGroup
                                    key={`index-${permissionGroup.id}`}
                                    title={permissionGroup.label}
                                    //description='Lista de permisos asignados al usuario'
                                    className='pt-6 mt-6 border-t border-gray-200'
                                    childClassName='col-span-full grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 @4xl:col-span-8 @4xl:gap-5 xl:gap-7'
                                >
                                    <div key={permissionGroup.id} className={cn([atLeastOneSelected ? '' : 'hidden absolute', "col-span-full"])}>
                                        <ShowPermissionAccordion
                                            group={permissionGroup}
                                            selectAll={[]}
                                            selectedPermissions={selectedPermissions}
                                        />
                                    </div>
                                </FormGroup>
                            )
                        })
                        : <Loader height='50vh' />
                    }
                </div>
            </div>
        </>
    );
};

export default UserPermissions;